import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from "react-native";
import {
  applyActiveModeBlockAll,
  behavioralToPickerId,
  classifyAndUpsertApp,
  classifyAppHeuristicOnly,
  clearUserDisturbingBlock,
  evaluateLockbox,
  getAppLibraryStatus,
  getDistractingAppsForActiveMode,
  isCategoryBlockedInMode,
  LIBRARY_CATEGORY_GROUPS,
  pickerIdToBehavioral,
  setAppCategory,
  setAppForceAllow,
  setCategoryBlockedInActiveMode,
  setGroupCategoriesBlockedInActiveMode,
  setUserDisturbingBlock,
  useLockbox,
} from "../lockbox";
import { getInstalledAppsNative, lockboxEnforcementSupported } from "../platform/lockboxBridge";
import { CategoryPicker } from "./CategoryPicker";
import { ModeDistractingBanner } from "./ModeDistractingBanner";
import { AppReviewWizard } from "./AppReviewWizard";
import { pickerCategoryLabel } from "../lockbox/utils/categoryPickerCatalog.js";
import { touchUI } from "../utils/touchUI.js";

const CATEGORY_LABELS = {
  navigation: "Navigation",
  banking: "Banking",
  productivity: "Productivity",
  learning: "Education",
  utility: "Utility",
  work_comms: "Work & messaging",
  anime_streaming: "Anime streaming",
  manga_reading: "Manga / webtoon",
  shortform_feed: "Short-form video",
  gaming_session: "Gaming",
  entertainment_discovery: "Entertainment",
  unknown: "Other",
};

const MODE_LABELS = { normal: "Normal", work: "Work", sleep: "Sleep", strict: "Strict" };

/**
 * App Library — grouped categories, block-all toggles, AI suggestions (View B + C).
 */
export function AppLibraryScreen({ onBack, onOpenLockboxLab, hideBack = false }) {
  const state = useLockbox();
  const [viewMode, setViewMode] = useState("grouped");
  const [installed, setInstalled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [editPickerId, setEditPickerId] = useState("other");
  const [editDisturbing, setEditDisturbing] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() =>
    Object.fromEntries(LIBRARY_CATEGORY_GROUPS.map((g) => [g.id, true])),
  );

  const loadInstalled = useCallback(async () => {
    setLoading(true);
    try {
      if (Platform.OS === "android" && lockboxEnforcementSupported()) {
        const rows = await getInstalledAppsNative();
        rows.sort((a, b) => a.label.localeCompare(b.label));
        setInstalled(rows);
      } else {
        setInstalled([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInstalled();
  }, [loadInstalled]);

  useEffect(() => {
    setBannerDismissed(false);
  }, [state.activeModeId]);

  const plan = useMemo(() => {
    const world = {
      now: new Date(),
      activeModeId: state.activeModeId,
      emergency: state.emergency,
      apps: state.inventory,
    };
    return evaluateLockbox(state.rulepack, world);
  }, [state.rulepack, state.activeModeId, state.emergency, state.inventory]);

  const libraryApps = useMemo(() => {
    const byPkg = new Map(
      state.inventory
        .filter((a) => a.packageName || a.bundleId)
        .map((a) => [(a.packageName || a.bundleId || "").toLowerCase(), a]),
    );

    /** @type {import('../lockbox/domain/types.js').LockboxAppEntity[]} */
    const merged = [];

    for (const row of installed) {
      const key = row.packageName.toLowerCase();
      const existing = byPkg.get(key);
      if (existing) {
        merged.push(existing);
      } else {
        const quick = classifyAppHeuristicOnly(row.packageName, row.label);
        merged.push({
          id: `transient_${key}`,
          displayName: row.label,
          packageName: row.packageName,
          classification: quick.classification,
          nativeRef: null,
          _transient: true,
        });
      }
    }

    for (const app of state.inventory) {
      const key = (app.packageName || app.bundleId || "").toLowerCase();
      if (!key) merged.push(app);
      else if (!installed.some((i) => i.packageName.toLowerCase() === key)) {
        merged.push(app);
      }
    }

    return merged.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [installed, state.inventory]);

  const appsByGroup = useMemo(() => {
    /** @type {Record<string, typeof libraryApps>} */
    const map = Object.fromEntries(LIBRARY_CATEGORY_GROUPS.map((g) => [g.id, []]));
    for (const app of libraryApps) {
      const cat = app.classification?.primaryCategory || "unknown";
      const group = LIBRARY_CATEGORY_GROUPS.find((g) => g.categories.includes(cat)) || LIBRARY_CATEGORY_GROUPS.find((g) => g.id === "other");
      if (group) map[group.id].push(app);
    }
    for (const g of LIBRARY_CATEGORY_GROUPS) {
      map[g.id].sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    return map;
  }, [libraryApps]);

  const distractingApps = useMemo(
    () => getDistractingAppsForActiveMode(state, libraryApps, plan),
    [state, libraryApps, plan],
  );

  const modeIsFocus = state.activeModeId !== "normal";

  const selected = libraryApps.find((a) => a.id === selectedId);

  useEffect(() => {
    if (!selected) return;
    setEditPickerId(behavioralToPickerId(selected.classification?.primaryCategory || "unknown"));
    setEditDisturbing(!!selected.classification?.userFlaggedSeverity);
  }, [selectedId, selected?.classification?.primaryCategory, selected?.classification?.userFlaggedSeverity]);

  const ensureInInventory = async (app) => {
    if (app._transient) {
      return classifyAndUpsertApp({
        displayName: app.displayName,
        packageName: app.packageName,
      });
    }
    return app;
  };

  const isGroupBlockedInMode = (group) =>
    group.categories.length > 0 &&
    group.categories.every((cat) => isCategoryBlockedInMode(state.activeModeId, cat));

  const onToggleBlockAllGroup = (group, value) => {
    setGroupCategoriesBlockedInActiveMode(group.categories, value);
  };

  const handleReviewSave = async (app, { pickerCategoryId, disturbing, blockInMode }) => {
    const row = await ensureInInventory(app);
    const behavioral = pickerIdToBehavioral(pickerCategoryId);
    setAppCategory(row.id, behavioral);
    if (disturbing) setUserDisturbingBlock(row.id);
    else clearUserDisturbingBlock(row.id);
    if (blockInMode) {
      setCategoryBlockedInActiveMode(behavioral, true);
    } else {
      setAppForceAllow(row.id);
    }
  };

  const isAppBlocked = (app) => {
    const status = getAppLibraryStatus(app, plan, state.rulepack, state.activeModeId);
    return (
      status.badge === "auto_blocked" ||
      status.badge === "user_blocked" ||
      status.badge === "mode_blocked"
    );
  };

  const renderAppControls = (app) => {
    const status = getAppLibraryStatus(app, plan, state.rulepack, state.activeModeId);
    const blocked = isAppBlocked(app);
    const sev = app.classification?.contentSeverity || "unknown";

    return (
      <View key={app.id} style={styles.appControlRow}>
        <Pressable style={styles.appControlMain} onPress={() => setSelectedId(app.id)}>
          <View style={styles.rowIcon}>
            <Text style={styles.rowIconText}>{app.displayName.charAt(0).toUpperCase()}</Text>
          </View>
          <View style={styles.rowBody}>
            <Text style={styles.rowTitle}>{app.displayName}</Text>
            <Text style={[styles.badge, { color: status.color }]}>{status.label}</Text>
            {sev !== "unknown" ? <Text style={styles.sev}>Severity: {sev}</Text> : null}
          </View>
        </Pressable>
        <View style={styles.appToggles}>
          <Pressable
            style={[styles.miniBtn, blocked ? styles.miniBtnOn : null]}
            onPress={async () => {
              const row = await ensureInInventory(app);
              if (blocked) {
                setAppForceAllow(row.id);
              } else {
                setUserDisturbingBlock(row.id);
              }
            }}
          >
            <Text style={[styles.miniBtnText, blocked ? styles.miniBtnTextOn : null]}>Block</Text>
          </Pressable>
          <Pressable
            style={[styles.miniBtn, !blocked ? styles.miniBtnAllowOn : null]}
            onPress={async () => {
              const row = await ensureInInventory(app);
              setAppForceAllow(row.id);
            }}
          >
            <Text style={[styles.miniBtnText, !blocked ? styles.miniBtnTextOn : null]}>Allow</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderCategoryGroup = (group) => {
    const apps = appsByGroup[group.id] || [];
    const expanded = expandedGroups[group.id] !== false;
    const blockAllOn = isGroupBlockedInMode(group);

    return (
      <View key={group.id} style={styles.categoryGroup}>
        <Pressable
          style={styles.groupHeader}
          onPress={() => setExpandedGroups((prev) => ({ ...prev, [group.id]: !expanded }))}
        >
          <View style={styles.groupHeaderLeft}>
            <Text style={styles.groupEmoji}>{group.emoji}</Text>
            <View>
              <Text style={styles.groupTitle}>{group.title}</Text>
              <Text style={styles.groupHint}>{group.hint}</Text>
              <Text style={styles.groupCount}>
                {apps.length} app{apps.length === 1 ? "" : "s"}
                {apps.length > 0 ? ` · ${apps.map((a) => a.displayName).slice(0, 3).join(" · ")}${apps.length > 3 ? "…" : ""}` : ""}
              </Text>
            </View>
          </View>
          <Text style={styles.groupChev}>{expanded ? "▼" : "▶"}</Text>
        </Pressable>

        <View style={styles.blockAllRow}>
          <Text style={styles.blockAllLabel}>Block all in category ({MODE_LABELS[state.activeModeId] || state.activeModeId} mode)</Text>
          <Switch
            value={blockAllOn}
            onValueChange={(v) => onToggleBlockAllGroup(group, v)}
            trackColor={{ false: "#d1d5db", true: "#f87171" }}
            thumbColor="#fff"
          />
        </View>

        {expanded ? (
          <View style={styles.groupApps}>
            {apps.length === 0 ? (
              <Text style={styles.mutedSmall}>No apps in this category yet. Add from Lockbox Lab or device list.</Text>
            ) : (
              apps.map(renderAppControls)
            )}
          </View>
        ) : null}
      </View>
    );
  };

  const renderFlatRow = (app) => {
    const status = getAppLibraryStatus(app, plan, state.rulepack, state.activeModeId);
    return (
      <Pressable key={app.id} style={styles.row} onPress={() => setSelectedId(app.id)}>
        <View style={styles.rowIcon}>
          <Text style={styles.rowIconText}>{app.displayName.charAt(0).toUpperCase()}</Text>
        </View>
        <View style={styles.rowBody}>
          <Text style={styles.rowTitle}>{app.displayName}</Text>
          <Text style={styles.rowMeta}>{CATEGORY_LABELS[app.classification.primaryCategory] || app.classification.primaryCategory}</Text>
          <Text style={[styles.badge, { color: status.color }]}>{status.label}</Text>
        </View>
        <Text style={styles.chev}>›</Text>
      </Pressable>
    );
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.title}>App Library</Text>
      <Text style={styles.sub}>
        Mode: {MODE_LABELS[state.activeModeId] || state.activeModeId}
        {Platform.OS !== "android" ? " · install list on Android" : ""}
      </Text>

      {modeIsFocus && !bannerDismissed && distractingApps.length > 0 ? (
        <ModeDistractingBanner
          modeId={state.activeModeId}
          apps={distractingApps}
          onBlockAll={() => {
            applyActiveModeBlockAll();
            Alert.alert("Categories blocked", "Mode blocklist updated for distracting genres.");
          }}
          onReviewEach={() => setReviewOpen(true)}
          onDismiss={() => setBannerDismissed(true)}
        />
      ) : null}

      <View style={styles.tabs}>
        <Pressable
          style={[styles.tab, viewMode === "grouped" ? styles.tabOn : null]}
          onPress={() => setViewMode("grouped")}
        >
          <Text style={[styles.tabText, viewMode === "grouped" ? styles.tabTextOn : null]}>By category</Text>
        </Pressable>
        <Pressable
          style={[styles.tab, viewMode === "list" ? styles.tabOn : null]}
          onPress={() => setViewMode("list")}
        >
          <Text style={[styles.tabText, viewMode === "list" ? styles.tabTextOn : null]}>All apps</Text>
        </Pressable>
        <Pressable
          style={[styles.tab, viewMode === "suggestions" ? styles.tabOn : null]}
          onPress={() => setViewMode("suggestions")}
        >
          <Text style={[styles.tabText, viewMode === "suggestions" ? styles.tabTextOn : null]}>Suggestions</Text>
        </Pressable>
      </View>

      {loading ? <Text style={styles.muted}>Loading installed apps…</Text> : null}

      <ScrollView style={styles.scroll}>
        {viewMode === "grouped"
          ? LIBRARY_CATEGORY_GROUPS.map(renderCategoryGroup)
          : viewMode === "list"
            ? libraryApps.map(renderFlatRow)
            : modeIsFocus && distractingApps.length > 0
              ? distractingApps.map((app) => (
                  <Pressable key={app.id} style={styles.row} onPress={() => setSelectedId(app.id)}>
                    <View style={styles.rowBody}>
                      <Text style={styles.rowTitle}>{app.displayName}</Text>
                      <Text style={styles.rowMeta}>
                        {pickerCategoryLabel(behavioralToPickerId(app.classification?.primaryCategory || "unknown"))}
                      </Text>
                    </View>
                    <Text style={styles.chev}>›</Text>
                  </Pressable>
                ))
              : (
                  <Text style={styles.muted}>
                    {modeIsFocus
                      ? "No distracting apps to suggest — genres may already be blocked or library is empty."
                      : "Switch to Work, Sleep, or Strict mode in Lockbox Lab to see suggestions."}
                  </Text>
                )}
        {libraryApps.length === 0 && !loading && viewMode !== "suggestions" ? (
          <Text style={styles.muted}>
            No apps yet. Open Lockbox Lab → + From device (Android) or add demo apps.
          </Text>
        ) : null}
      </ScrollView>

      <AppReviewWizard
        visible={reviewOpen}
        apps={distractingApps}
        onClose={() => setReviewOpen(false)}
        onSaveApp={handleReviewSave}
      />

      {!hideBack && onBack ? (
        <Pressable style={styles.backBtn} onPress={onBack}>
          <Text style={styles.backBtnText}>Back</Text>
        </Pressable>
      ) : null}

      {selected ? (
        <Modal visible animationType="slide" transparent onRequestClose={() => setSelectedId(null)}>
          <View style={styles.detailOverlay}>
            <ScrollView style={styles.detailSheet} contentContainerStyle={styles.detailContent}>
              <Text style={styles.detailTitle}>{selected.displayName}</Text>
              {selected.packageName ? <Text style={styles.detailPkg}>{selected.packageName}</Text> : null}

              <CategoryPicker
                detectedCategory={selected.classification?.primaryCategory || "unknown"}
                pickerCategoryId={editPickerId}
                onPickerCategoryChange={setEditPickerId}
                disturbing={editDisturbing}
                onDisturbingChange={setEditDisturbing}
              />

              <Text style={styles.detailLabel}>AI / scan severity</Text>
              <Text style={styles.detailValue}>{selected.classification.contentSeverity || "unknown"}</Text>

              <Pressable
                style={styles.saveBtn}
                onPress={async () => {
                  const row = await ensureInInventory(selected);
                  setAppCategory(row.id, pickerIdToBehavioral(editPickerId));
                  if (editDisturbing) setUserDisturbingBlock(row.id);
                  else clearUserDisturbingBlock(row.id);
                  setSelectedId(null);
                }}
              >
                <Text style={styles.saveBtnText}>Save</Text>
              </Pressable>

              <Pressable
                style={styles.allowBtn}
                onPress={async () => {
                  const row = await ensureInInventory(selected);
                  setAppForceAllow(row.id);
                  setSelectedId(null);
                }}
              >
                <Text style={styles.allowBtnText}>Always allow</Text>
              </Pressable>

              <Pressable style={styles.closeDetail} onPress={() => setSelectedId(null)}>
                <Text style={styles.closeDetailText}>Close</Text>
              </Pressable>
            </ScrollView>
          </View>
        </Modal>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1 },
  title: { fontSize: 22, fontWeight: "800", color: "#111", textAlign: "center", marginBottom: 4 },
  sub: { fontSize: 12, color: "#6b7280", textAlign: "center", marginBottom: 10 },
  suggestionCard: {
    backgroundColor: "#fffbeb",
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#fde68a",
  },
  suggestionLabel: { fontSize: 11, fontWeight: "800", color: "#b45309", marginBottom: 8 },
  suggestionBlock: { marginBottom: 10 },
  suggestionTitle: { fontSize: 14, fontWeight: "700", color: "#92400e" },
  suggestionBody: { fontSize: 12, color: "#78350f", marginTop: 4, lineHeight: 18 },
  suggestionActions: { flexDirection: "row", alignItems: "center", gap: 12, marginTop: 8 },
  suggestionBtn: { backgroundColor: "#d97706", paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  suggestionBtnText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  suggestionDismiss: { color: "#92400e", fontWeight: "600", fontSize: 12 },
  tabs: { flexDirection: "row", gap: 8, marginBottom: 10 },
  tab: { flex: 1, paddingVertical: 8, borderRadius: 8, backgroundColor: "#f3f4f6", alignItems: "center", minWidth: 0 },
  tabOn: { backgroundColor: "#4f46e5" },
  tabText: { fontWeight: "600", color: "#374151", fontSize: 13 },
  tabTextOn: { color: "#fff" },
  scroll: { flex: 1, marginBottom: 8 },
  categoryGroup: {
    marginBottom: 14,
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    overflow: "hidden",
  },
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    backgroundColor: "#f8fafc",
  },
  groupHeaderLeft: { flexDirection: "row", alignItems: "flex-start", flex: 1 },
  groupEmoji: { fontSize: 22, marginRight: 10 },
  groupTitle: { fontSize: 15, fontWeight: "800", color: "#111" },
  groupHint: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  groupCount: { fontSize: 10, color: "#9ca3af", marginTop: 4 },
  groupChev: { fontSize: 14, color: "#6b7280", marginLeft: 8 },
  blockAllRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#e5e7eb",
    backgroundColor: "#fef2f2",
  },
  blockAllLabel: { flex: 1, fontSize: 12, fontWeight: "600", color: "#991b1b", paddingRight: 8 },
  groupApps: { paddingHorizontal: 8, paddingBottom: 8 },
  appControlRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#f3f4f6",
  },
  appControlMain: { flex: 1, flexDirection: "row", alignItems: "center" },
  appToggles: { flexDirection: "row", gap: 6 },
  miniBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: "#f3f4f6",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  miniBtnOn: { backgroundColor: "#dc2626", borderColor: "#dc2626" },
  miniBtnAllowOn: { backgroundColor: "#16a34a", borderColor: "#16a34a" },
  miniBtnText: { fontSize: 11, fontWeight: "700", color: "#374151" },
  miniBtnTextOn: { color: "#fff" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#e5e7eb",
  },
  rowIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#e0e7ff",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  rowIconText: { fontWeight: "800", color: "#4338ca", fontSize: 16 },
  rowBody: { flex: 1 },
  rowTitle: { fontSize: 15, fontWeight: "700", color: "#111" },
  rowMeta: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  badge: { fontSize: 11, fontWeight: "700", marginTop: 4 },
  sev: { fontSize: 10, color: "#9ca3af", marginTop: 2 },
  chev: { fontSize: 20, color: "#9ca3af" },
  muted: { color: "#6b7280", fontStyle: "italic", padding: 12 },
  mutedSmall: { color: "#9ca3af", fontSize: 11, fontStyle: "italic", padding: 8 },
  backBtn: {
    backgroundColor: "#111",
    minHeight: touchUI.minHit,
    paddingVertical: touchUI.primaryPadV,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  backBtnText: { color: "#fff", fontWeight: "800", fontSize: touchUI.primaryFont },
  detailOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  detailSheet: {
    maxHeight: "85%",
    backgroundColor: "#fff",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  detailContent: { padding: 16, paddingBottom: 32 },
  saveBtn: {
    marginTop: 12,
    backgroundColor: "#4f46e5",
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
  },
  saveBtnText: { color: "#fff", fontWeight: "700" },
  detailTitle: { fontSize: 18, fontWeight: "800" },
  detailPkg: { fontSize: 12, color: "#6b7280", marginBottom: 8 },
  detailLabel: { fontSize: 12, fontWeight: "700", color: "#374151", marginTop: 8 },
  detailValue: { fontSize: 14, color: "#111" },
  catScroll: { marginVertical: 8, maxHeight: 44 },
  catChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: "#f3f4f6",
    marginRight: 6,
  },
  catChipOn: { backgroundColor: "#4f46e5" },
  catChipText: { fontSize: 11, fontWeight: "600", color: "#374151" },
  catChipTextOn: { color: "#fff" },
  dangerBtn: {
    marginTop: 12,
    backgroundColor: "#dc2626",
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
  },
  dangerBtnText: { color: "#fff", fontWeight: "700" },
  allowBtn: {
    marginTop: 8,
    backgroundColor: "#16a34a",
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
  },
  allowBtnText: { color: "#fff", fontWeight: "700" },
  closeDetail: { marginTop: 10, alignItems: "center" },
  closeDetailText: { color: "#2563eb", fontWeight: "600" },
});
